
import React from 'react';

export interface UserLevel {
  level: 'Inactive' | 'Saver' | 'Organizer' | 'Discoverer' | 'Curator';
  level_number: number;
}

interface UserLevelBadgeProps {
  level: UserLevel['level'];
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const levelConfig = {
  'Inactive': {
    shape: 'circle-outline',
    color: '#FF6B00',
    bgColor: '#FFF7ED',
    textColor: '#EA580C'
  },
  'Saver': {
    shape: 'circle',
    color: '#00F6C5',
    bgColor: '#ECFDF5',
    textColor: '#065F46'
  },
  'Organizer': {
    shape: 'square',
    color: '#4EB0FF',
    bgColor: '#EFF6FF',
    textColor: '#1E40AF'
  },
  'Discoverer': {
    shape: 'pentagon',
    color: '#B164F8',
    bgColor: '#F3E8FF',
    textColor: '#7C3AED'
  },
  'Curator': {
    shape: 'star',
    color: 'linear-gradient(135deg, #0073FF 0%, #FF00A4 100%)',
    bgColor: '#FEF7FF',
    textColor: '#86198F'
  }
};

const BadgeShape: React.FC<{ level: UserLevel['level']; size: string }> = ({ level, size }) => {
  const config = levelConfig[level];
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
  
  if (config.shape === 'circle-outline') {
    return (
      <div 
        className={`${sizeClass} rounded-full border-2 bg-transparent`}
        style={{ borderColor: config.color }}
      />
    );
  }
  
  if (config.shape === 'circle') {
    return (
      <div 
        className={`${sizeClass} rounded-full`}
        style={{ backgroundColor: config.color }}
      />
    );
  }
  
  if (config.shape === 'square') {
    return (
      <div 
        className={`${sizeClass} rounded-sm`}
        style={{ backgroundColor: config.color }}
      />
    );
  }
  
  if (config.shape === 'pentagon') {
    return (
      <svg className={sizeClass} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2L22 9L18 20H6L2 9L12 2Z"
          fill={config.color}
          style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}
        />
      </svg>
    );
  }
  
  if (config.shape === 'star') {
    return (
      <svg className={sizeClass} viewBox="0 0 24 24" fill="none">
        <defs>
          <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0073FF" />
            <stop offset="100%" stopColor="#FF00A4" />
          </linearGradient>
        </defs>
        <path
          d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
          fill="url(#starGradient)"
        />
      </svg>
    );
  }
  
  return null;
};

export const UserLevelBadge: React.FC<UserLevelBadgeProps> = ({ 
  level, 
  size = 'md', 
  showLabel = false 
}) => {
  const config = levelConfig[level];
  
  return (
    <div className="flex items-center gap-2">
      <BadgeShape level={level} size={size} />
      {showLabel && (
        <span 
          className={`text-xs font-medium ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
          style={{ color: config.textColor }}
        >
          {level}
        </span>
      )}
    </div>
  );
};
